import { useTranslation } from "react-i18next";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { MyBooking } from "@models";
import { salonTime, SharedPopconfirm, TenantAvatar } from "@shared";
import { useStore } from "@stores";
import { Button, Col, Row, Tag, Typography } from "antd";
import { observer } from "mobx-react";
import styled from "styled-components";
import "./index.css";

const { Text } = Typography;

/* Soft-filled cancel button — matches Canceled status tag palette in the
   same card for visual consistency, instead of antd's harsh solid danger. */
const CancelButton = styled(Button)`
  &.ant-btn {
    background: #fee2e2;
    border: 1px solid #fecaca;
    color: #991b1b;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    box-shadow: none;

    &:hover {
      background: #fecaca !important;
      border-color: #fca5a5 !important;
      color: #991b1b !important;
    }

    &:active {
      background: #fca5a5 !important;
    }

    > .ant-wave {
      display: none;
    }
  }
`;

interface IProps {
  booking: MyBooking;
  allowAction?: boolean;
}

export const MyBookingCard: React.FC<IProps> = observer(
  ({ booking, allowAction }) => {
    const { t } = useTranslation();
    const { bookingStore } = useStore();

    const statusConfig = {
      Pending: {
        label: t("StatusPending"),
        color: "#FEF3C7",
        textColor: "#92400E",
        border: "#FDE68A",
      },
      Confirmed: {
        label: t("StatusConfirmed"),
        color: "#D1FAE5",
        textColor: "#065F46",
        border: "#A7F3D0",
      },
      Canceled: {
        label: t("StatusCanceled"),
        color: "#FEE2E2",
        textColor: "#991B1B",
        border: "#FECACA",
      },
      NoShow: {
        label: t("StatusNoShow"),
        color: "#F3F4F6",
        textColor: "#4B5563",
        border: "#D1D5DB",
      },
      Unassigned: {
        label: t("Unscheduled"),
        color: "#FBD9E3",
        textColor: "#6B0B2E",
        border: "#F6B3C7",
      },
    };
    const { myBookingStatusLoading, updateMyBookingStatus } = bookingStore;

    const showCancelButton =
      booking.start &&
      (booking.status === "Pending" || booking.status === "Confirmed") &&
      allowAction;

    const statusKey = !booking.start
      ? "Unassigned"
      : (booking.status as keyof typeof statusConfig);
    const status = statusConfig[statusKey] ?? statusConfig.Canceled;

    return (
      <div className="booking-card">
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 14 }}
        >
          <Tag
            style={{
              color: status.textColor,
              background: status.color,
              border: `1px solid ${status.border}`,
              borderRadius: 20,
              fontWeight: 600,
              fontSize: 12,
              padding: "2px 10px",
              lineHeight: "22px",
            }}
          >
            {status.label}
          </Tag>
          <TenantAvatar
            logo={booking.tenant.logo}
            name={booking.tenant.name}
            size={60}
          />
        </Row>

        {/* Content */}
        <div style={{ flex: 1, marginBottom: 14 }}>
          <Text
            strong
            style={{
              display: "block",
              fontSize: 15,
              color: "#1E293B",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginBottom: 8,
            }}
          >
            {booking.title}
          </Text>

          <Row gutter={[0, 6]}>
            <Col span={24}>
              {booking.start ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span
                    style={{
                      fontSize: 13,
                      color: "#64748B",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <CalendarOutlined style={{ color: "#D81B60" }} />
                    {salonTime(booking.start, booking.tenant?.timeZone).format("DD/MM/YY")}
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "2px 10px",
                      background: "#FDF0F4",
                      border: "1px solid #FBD9E3",
                      borderRadius: 8,
                    }}
                  >
                    <ClockCircleOutlined style={{ color: "#D81B60", fontSize: 12 }} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#1E293B" }}>
                      {salonTime(booking.start, booking.tenant?.timeZone).format("HH:mm")}
                    </span>
                    <span style={{ fontSize: 12, color: "#94A3B8" }}>→</span>
                    <span style={{ fontSize: 12, color: "#64748B" }}>
                      {salonTime(booking.end, booking.tenant?.timeZone).format("HH:mm")}
                    </span>
                  </span>
                </div>
              ) : (
                <Text
                  style={{
                    fontSize: 13,
                    color: "#64748B",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <CalendarOutlined style={{ color: "#D81B60" }} />
                  {booking.time ? booking.time : t("Unscheduled")}
                </Text>
              )}
            </Col>
            {booking.fullName && (
              <Col span={24}>
                <Text
                  style={{
                    fontSize: 13,
                    color: "#64748B",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <UserOutlined style={{ color: "#D81B60" }} />
                  {booking.fullName}
                </Text>
              </Col>
            )}
          </Row>
        </div>

        {/* Footer */}
        <Row justify="space-between" align="middle">
          <Col>
            <Text
              style={{
                fontSize: 12,
                color: "#94A3B8",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <ClockCircleOutlined />
              {booking.tenant.name}
            </Text>
          </Col>
          <Col>
            {showCancelButton && (
              <SharedPopconfirm
                loading={myBookingStatusLoading}
                onConfirm={() =>
                  updateMyBookingStatus({ id: booking.id, status: "Canceled" })
                }
              >
                <CancelButton size="small">
                  {t("CancelBooking")}
                </CancelButton>
              </SharedPopconfirm>
            )}
          </Col>
        </Row>
      </div>
    );
  },
);
